/**
 * Performance Dashboard Permission Constants
 *
 * Three tiers of visibility:
 *
 *   self_performance   → Recruiter sees only their own rows
 *   team_performance   → Team Lead sees their team's rows
 *   org_performance    → HR / Admin sees every recruiter
 *
 * Backend must include these subjects in the user's `abilities` array.
 *
 * Example abilities payload from the server:
 *   [
 *     { action: 'read', subject: 'performance_self'  },   // recruiter
 *     { action: 'read', subject: 'performance_team'  },   // team lead
 *     { action: 'read', subject: 'performance_org'   },   // admin/HR
 *   ]
 */

export const PERFORMANCE_PERMISSIONS = {
  SELF: 'performance_self',   // own data only
  TEAM: 'performance_team',   // own team only (Team Lead = current user's name)
  ORG:  'performance_org',    // all data
}

/**
 * Reference role → abilities map for the backend team.
 * The frontend uses $can() to read abilities set by the server.
 */
export const PERFORMANCE_ROLE_ABILITIES = {
  // Regular recruiter / talent advisor
  recruiter: [
    { action: 'read', subject: PERFORMANCE_PERMISSIONS.SELF },
  ],

  // Team Lead / Account Manager / Program Manager
  team_lead: [
    { action: 'read', subject: PERFORMANCE_PERMISSIONS.SELF },
    { action: 'read', subject: PERFORMANCE_PERMISSIONS.TEAM },
  ],

  // HR, HR Director, Admin, Master
  admin: [
    { action: 'read', subject: PERFORMANCE_PERMISSIONS.SELF },
    { action: 'read', subject: PERFORMANCE_PERMISSIONS.TEAM },
    { action: 'read', subject: PERFORMANCE_PERMISSIONS.ORG  },
  ],
}

/**
 * Derive the highest permission scope available to the current user.
 * Call this from a Vue component with `this.$can`.
 *
 * @param {Function} $can  - Vue CASL $can helper
 * @returns {'org'|'team'|'self'|null}
 */
export function resolvePerformanceScope($can) {
  if ($can('read', PERFORMANCE_PERMISSIONS.ORG))  return 'org'
  if ($can('read', PERFORMANCE_PERMISSIONS.TEAM)) return 'team'
  if ($can('read', PERFORMANCE_PERMISSIONS.SELF)) return 'self'
  // Fallback: if legacy manage:all or manage:performance_dashboard is present
  if ($can('manage', 'all') || $can('read', 'performance_dashboard')) return 'org'
  return null
}

/**
 * Human-readable labels for each scope.
 */
export const SCOPE_META = {
  org:  { label: 'Organisation',   icon: 'GlobeIcon',  variant: 'primary'  },
  team: { label: 'My Team',        icon: 'UsersIcon',  variant: 'warning'  },
  self: { label: 'My Performance', icon: 'UserIcon',   variant: 'success'  },
}
